<template>
  <div>
    <div>
      <Loader
        v-if="trainLoader && updateLoader && initialSetupDone"
        class="mr-2 mt-6"
      />
      <template v-else-if="initialSetupDone">
        <v-card v-if="$isSectionEnabled('settings','train_data')" class="elevation-0">
          <v-card-title class="font-weight-bold">
            {{ `${$vuetify.lang.t("$vuetify.project_settings.train_data")}` }}
          </v-card-title>
          <v-divider></v-divider>
          <v-row>
            <v-col class="mx-2">
              <template>
                <ItemsTrain
                  @updateComplete="handleTrainHistoryTableComplete"
                />

                <div v-if="mapper.detail">
                  <v-divider></v-divider>
                  <ImagesTrain
                    :mapper="mapper"
                    :taskHistoryData="taskHistoryData"
                    @updateComplete="handleTrainHistoryTableComplete"
                  />
                </div>

                <v-divider></v-divider>
                <RanksTrain @updateComplete="handleTrainHistoryTableComplete" />
              </template>
            </v-col>
          </v-row>
        </v-card>

        <v-card v-if="$isSectionEnabled('settings','train_history')" class="elevation-0">
          <v-card-title class="text-h6 font-weight-bold">
            {{ `${$vuetify.lang.t("$vuetify.project_settings.train_history")}` }}
          </v-card-title>
          <v-divider></v-divider>
          <v-row class="mx-2 mb-3">
            <v-col cols="12" md="6"></v-col>
          </v-row>
          <div>
            <Loader v-if="isTrainHistoryLoading" class="mr-2 mt-6" />
            <template v-else>
              <v-col xs="12" md="4">
                <v-select
                  v-model="filterTrainHistoryType"
                  :items="filterTrainHistoryOptions"
                  :label="$vuetify.lang.t('$vuetify.project_settings.train_type')"
                  item-text="name"
                  item-value="value"
                  @change="onFilterTrainTypeChange"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-data-table
                :headers="trainHistoryHeaders"
                :items="taskHistoryData"
                :sort-by="['created_at']"
                :sort-desc="[true]"
                :server-items-length="totalNumberOfTrains"
                :footer-props="{
                  'items-per-page-options': [5, 10, 15, 50, 100],
                }"
                :options.sync="paginateTrainHistoryObj"
                @update:options="paginateTrainHistory"
              >
                <template v-slot:item.name="{ item }">
                  {{ item.name ? capitalizeFirstLetter(item.name) : "N/A" }}
                </template>

                <template v-slot:item.started_at="{ item }">
                  {{
                    item.started_at ? formatDateTime(item.started_at) : "N/A"
                  }}
                </template>

                <template v-slot:item.last_updated_at="{ item }">
                  {{
                    item.last_updated_at
                      ? formatDateTime(item.last_updated_at)
                      : "N/A"
                  }}
                </template>

                <template v-slot:item.message="{ item }">
                  {{ item.message ? item.message : "N/A" }}
                </template>

                <template v-slot:item.scheduled_by="{ item }">
                  {{ item.scheduled_by ? item.scheduled_by : "N/A" }}
                </template>

                <template v-slot:item.status="{ item }">
                  {{ item.status ? item.status : "N/A" }}
                </template>
              </v-data-table>
            </template>
          </div>
        </v-card>

        <!-- <template>
          <v-expansion-panels :value="0">
              <v-expansion-panel>
                  <v-expansion-panel-header>
                      <div class="text-h6 font-weight-bold ml-n2">{{ `${$vuetify.lang.t("$vuetify.project_settings.train_data")}` }}</div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content>
                      <ItemsTrain @updateComplete="handleTrainHistoryTableComplete"/>
                    <v-divider></v-divider>
                      <ImagesTrain :mapper="mapper" @updateComplete="handleTrainHistoryTableComplete"/>
                    <v-divider></v-divider>
                      <RanksTrain @updateComplete="handleTrainHistoryTableComplete"/>
                  </v-expansion-panel-content>
              </v-expansion-panel>
          </v-expansion-panels>
        </template> -->

        <template>
          <UpdateRankSettings
            v-if="$isSectionEnabled('settings','update_rank_settings')"
            :rankSettings="rankSettings"
            @updateComplete="handleSettingsUpdate"
          />
          <UpdateMapper
            v-if="$isSectionEnabled('settings','update_mapper')"
            :mapper="mapper"
            @updateComplete="handleMapperUpdateComplete"            
          />
        </template>
      </template>
      <template
        v-else-if="
          selectedProject.segment.name === PROJECT_SEGMENTS.PERSONALIZER
        "
      >
        <div class="mt-8">
          <v-alert type="info" text border="left">
            {{
              `${$vuetify.lang.t("$vuetify.project_settings.settings_initial_setup_info")}`
            }}
          </v-alert>
        </div>
      </template>
      <template
        v-if="
          selectedProject.segment.name === PROJECT_SEGMENTS.PERSONALIZER ||
          selectedProject.segment.name === PROJECT_SEGMENTS.Maira
        "
      >
        <v-card
          v-if="selectedProject.solution_template.sandbox_location && $isSectionEnabled('settings','sandbox')"
          class="elevation-0"
        >
          <v-card-title class="font-weight-bold">
            {{ $vuetify.lang.t("$vuetify.common.sandbox_btn") }}
          </v-card-title>
          <v-divider></v-divider>
          <v-row>
            <v-col class="mx-2">
              <template>
                <div>
                  <v-btn
                    class="ma-2"
                    outlined
                    color="primary"
                    :href="selectedProject.solution_template.sandbox_location"
                    target="_blank"
                  >
                    {{ $vuetify.lang.t("$vuetify.common.sandbox_btn") }}
                  </v-btn>
                </div>
              </template>
            </v-col>
          </v-row>
        </v-card>
        <v-card
          v-if="selectedProject.solution_template.documentation_location && $isSectionEnabled('settings','documentation')"
          class="elevation-0"
        >
          <v-card-title class="font-weight-bold">{{
            $vuetify.lang.t("$vuetify.common.documentation_btn")
          }}</v-card-title>
          <v-divider></v-divider>
          <v-row>
            <v-col class="mx-2">
              <template>
                <div>
                  <v-btn
                    class="ma-2"
                    outlined
                    color="primary"
                    :href="
                      selectedProject.solution_template.documentation_location
                    "
                    target="_blank"
                  >
                    {{ $vuetify.lang.t("$vuetify.common.documentation_btn") }}
                  </v-btn>
                </div>
              </template>
            </v-col>
          </v-row>
        </v-card>
        <v-card
          v-if="selectedProject.solution_template.tutorial_location && $isSectionEnabled('settings','tutorial')"
          class="elevation-0"
        >
          <v-card-title class="font-weight-bold">{{
            $vuetify.lang.t("$vuetify.common.tutorial_btn")
          }}</v-card-title>
          <v-divider></v-divider>
          <v-row>
            <v-col class="mx-2">
              <template>
                <div>
                  <v-btn
                    class="ma-2"
                    outlined
                    color="primary"
                    :href="selectedProject.solution_template.tutorial_location"
                    target="_blank"
                  >
                    {{ $vuetify.lang.t("$vuetify.common.tutorial_btn") }}
                  </v-btn>
                </div>
              </template>
            </v-col>
          </v-row>
        </v-card>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";


import {
  ADD_ALERT,
  LOAD_ALL_CREDENTIAL,
  SAVE_CREDENTIAL,
  UPDATE_CREDENTIAL,
  GET_MAPPER,
  GET_RANK_SETTINGS,
  LOAD_TRAIN_TASKS,
  GET_PREVIOUSLY_SETUP_STATE,
} from "@/store/_actiontypes";

import { TEXT } from "@/components/common/input/_inputTypes";

import {
  FLOATING_ACTION_BUTTON,
  TEXT_BUTTON,
  RAISED_BUTTON,
} from "@/components/common/button/_buttontypes";
import ItemsTrain from "@/components/Projects/Train/ItemsTrain";
import ImagesTrain from "@/components/Projects/Train/ImagesTrain";
import RanksTrain from "@/components/Projects/Train/RanksTrain";
import UpdateRankSettings from "@/components/Projects/UpdateSettings/UpdateRankSettings";
import UpdateMapper from "@/components/Projects/UpdateSettings/UpdateMapper";

import { ACTION_BUTTON_GROUP } from "@/components/common/buttongroup/_buttongrouptypes";

import {
  capitalizeFirstLetter,
  formatDateTime,
} from "@/helpers/helper";
import { SEGMENTS } from "@/global/_constants";
import Loader from "@/components/common/loader/Loader";
import Button from "@/components/common/button/Button";
import ButtonGroup from "@/components/common/buttongroup/ButtonGroup";
import InputControl from "@/components/common/input/InputControl";
import ChatHeadTooltip from "@/components/common/tooltip/ChatHeadTooltip.vue";
import jwt_decode from "jwt-decode";
export default {
  props: {
    fromMairaTab: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  components: {
    Loader,
    ItemsTrain,
    ImagesTrain,
    RanksTrain,
    UpdateRankSettings,
    UpdateMapper,
  },
  data() {
    return {
      capitalizeFirstLetter,
      formatDateTime,
      TEXT: TEXT,
      FLOATING_ACTION_BUTTON: FLOATING_ACTION_BUTTON,
      TEXT_BUTTON: TEXT_BUTTON,
      RAISED_BUTTON: RAISED_BUTTON,
      ACTION_BUTTON_GROUP: ACTION_BUTTON_GROUP,
      isLoading: false,
      PROJECT_SEGMENTS: SEGMENTS,
      // TODO: Need to build text handling from a string.json file with different locale support
      alertText: {
        createNotAllow: this.$vuetify.lang.t(
          "$vuetify.project_settings.create_restricted_msg_txt"
        ),
        projectLocked: this.$vuetify.lang.t(
          "$vuetify.project_settings.project_locked_msg_txt"
        ),
      },
      mapper: {},
      rankSettings: {},
      trainLoader: false,
      updateLoader: false,
      isTrainHistoryLoading: false,
      trainHistoryHeaders: [
        {
          text: this.$vuetify.lang.t("$vuetify.table_header.name_header_txt"),
          align: "center",
          sortable: false,
          value: "name",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.job.started_at"),
          align: "center",
          sortable: false,
          value: "started_at",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.job.finished_at"),
          align: "center",
          sortable: false,
          value: "last_updated_at",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.table_header.status"),
          align: "center",
          sortable: false,
          value: "status",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.project.message"),
          align: "center",
          sortable: false,
          value: "message",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.project.generated_by"),
          align: "center",
          sortable: false,
          value: "scheduled_by",
        },
      ],
      totalNumberOfTrains: 0,
      defaultPagination: {
        page: 1,
        itemsPerPage: 5,
        sortBy: ["created_at"],
        sortDesc: [true],
        groupBy: [],
        groupDesc: [],
        mustSort: false,
        multiSort: false,
        descending: true,
        totalItems: 0,
      },
      paginateTrainHistoryObj: {
        descending: true,
        page: 1,
        itemsPerPage: 5, // -1 for All,
        totalItems: 0,
      },
      filterTrainHistoryType: "all_tasks",
      filterTrainHistoryOptions: [
        {
          name: this.$vuetify.lang.t("$vuetify.training.all"),
          value: "all_tasks"
        },
        {
          name: this.$vuetify.lang.t("$vuetify.training.items_train"),
          value: "item_train"
        },
        {
          name: this.$vuetify.lang.t("$vuetify.training.ranks_train"),
          value: "rank_train"
        },
        {
          name: this.$vuetify.lang.t("$vuetify.training.images_train"),
          value: "image_train"
        },
        {
          name: this.$vuetify.lang.t("$vuetify.training.insight_process"),
          value: "insight_process"
        },
      ],
      taskHistoryData: [],
      updateFlag: false,
    };
  },
  watch: {
    rankSettings: {
      handler(newRankSettings) {
        // Check to ensure newRankSettings is not null or undefined
        if (newRankSettings != null) {
          // Perform your logic that manipulates newRankSettings
          // console.log('Rank settings updated:', newRankSettings);
        } else {
          console.warn("rankSettings is null or undefined");
        }
      },
      immediate: true,
    },
    fromMairaTab: {
      handler(newVal, oldVal){
        if(newVal){
          this.credentialType = 'client';
          this.dialog = !this.activeClientKey;
        }
      },
      immediate: true
    },
  },
  computed: {
    ...mapState({
      selectedProject: (state) => state.project.selectedProject,
      allCredential: (state) => state.project.allCredential,
      trainTasks: (state) => state.project.trainTasks,
      organization: state => state.account.organization,
      user: (state) => state.account.user, // FIXME we shouldn't store and access user credentials from vuex store. Always use directly from localStorage
      activeClientKey() {
        let clientCredential = this.allCredential.find(
            credential => credential.key_type === 'client' && credential.status === 'active'
        );
        return clientCredential ? clientCredential.id : null;
      },
    }),
    ...mapGetters("loader", ["loading"]),
    ...mapGetters("project", ["credentials"]),
    initialSetupDone() {
      return (
        this.selectedProject?.setup_state &&
        this.selectedProject.setup_state.every(
          entry => entry.status === "success",
        )
      );
    },
  },
  beforeMount() {
    /* TODO: Here if we are calling three(3) API's at a time, 
     the recommender gets WORKER failed to load and get restarted.
     Need to handle this scenerio first. For now we are 
     calling the API with a small delay so that user experiences 
     is not get affected.

      // this.getMapper();
      // this.getRankSettings();
      // this.getTrainTasks(this.defaultPagination);
     */
    if (this.initialSetupDone) {
      this.getTrainTasks(this.defaultPagination);

      setTimeout(() => {
        this.getMapper();
        // setTimeout(() => {
        //   this.getRankSettings();
        // }, 800);
      }, 1000);
    }
  },
  methods: {
    ...mapActions("project", [
      LOAD_ALL_CREDENTIAL,
      SAVE_CREDENTIAL,
      UPDATE_CREDENTIAL,
      GET_MAPPER,
      GET_RANK_SETTINGS,
      LOAD_TRAIN_TASKS,
      GET_PREVIOUSLY_SETUP_STATE,
    ]),
    ...mapActions("alert", [ADD_ALERT]),
    getTrainTasks(options) {
      this.defaultPagination = this.deepCopy(options);
      this.isTrainHistoryLoading = true;
      this.LOAD_TRAIN_TASKS({
        project_id: this.selectedProject.id,
        train_type: this.filterTrainHistoryType,
        status: "all_status",
        start: (options.page - 1) * options.itemsPerPage,
        size: options.itemsPerPage,
      }).then(
        response => {
          this.totalNumberOfTrains = response.data.detail.total_train;
          this.taskHistoryData = response.data.detail.response;
          this.isTrainHistoryLoading = false;
        },
        error => {
          this.isTrainHistoryLoading = false;
        },
      );
    },
    getMapper() {
      this.isLoading = true;
      this.trainLoader = true;
      this.GET_MAPPER({
        project_id: this.selectedProject.id,
      }).then(
        response => {
          if (!this.updateFlag) {
            this.getRankSettings();
          }
          this.mapper = response.data;
          this.isLoading = false;
          this.trainLoader = false;
        },
        error => {
          this.isLoading = false;
          this.trainLoader = false;
        },
      );
    },
    getRankSettings() {
      this.updateLoader = true;
      this.GET_RANK_SETTINGS({
        project_id: this.selectedProject.id,
      }).then(
        response => {
          this.rankSettings = response.data;
          this.isLoading = false;
          this.updateLoader = false;
        },
        error => {
          this.isLoading = false;
          this.updateLoader = false;
        },
      );
    },
    resetValidation() {
      this.$refs.form.resetValidation();
    },
    paginateTrainHistory(options) {
      if (!this.deepEqual(options, this.defaultPagination)) {
        this.getTrainTasks(options);
      }
    },
    deepEqual(obj1, obj2) {
      if (obj1 === obj2) return true;

      if (
        typeof obj1 !== "object" ||
        obj1 === null ||
        typeof obj2 !== "object" ||
        obj2 === null
      )
        return false;

      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);

      if (keys1.length !== keys2.length) return false;

      for (const key of keys1) {
        if (!keys2.includes(key) || !this.deepEqual(obj1[key], obj2[key]))
          return false;
      }

      return true;
    },
    onFilterTrainTypeChange() {
      this.paginateTrainHistoryObj.page = 1;
      this.getTrainTasks(this.paginateTrainHistoryObj);
    },
    deepCopy(obj) {
      return JSON.parse(JSON.stringify(obj));
    },
    handleMapperUpdateComplete() {
      this.updateFlag = true;
      this.getMapper();
    },
    handleTrainHistoryTableComplete() {
      this.getTrainTasks(this.defaultPagination);
    },
    handleSettingsUpdate() {
      this.getPreviouslySetupState();
    },
    getPreviouslySetupState() {
      this.isLoading = true;
      this.isSetupStateLoading = true;
      this.GET_PREVIOUSLY_SETUP_STATE({
        project_id: this.$route.query.pid,
      }).then(
        response => {
          this.isSetupStateLoading = false;
          // Here we are updating global state : previouslySetupState
        },
        error => {
          this.isSetupStateLoading = false;
        },
      );
    },
    accountRoles() {
      return jwt_decode(this.user.access_token)["scope"];
    }
  },
};
</script>

<style scoped>
.banner-style {
  border-color: rgba(241, 101, 8, 0.986);
  border-width: 2px;
  border-radius: 5px !important;
  color: rgba(241, 101, 8, 0.986);
  background-color: #fff !important;
}
</style>
